// Changes to a wall run (3.20.0): doors added, changed and taken away; thickness and fill; the length of one wall.
// Each change is ONE command on meta.wall (or on the spine); the wall deriver then draws the wall again (wall.ts).
// A change that cannot be made is a sentence that says why. Pure: no store, no UI. tests/walls.test.ts covers it.
import type { Vec } from "../core/geometry";
import type { Doc, GroupEntity } from "../doc/entities";
import type { Command } from "../doc/commands";
import { newId } from "../doc/document";
import { DOOR_MARGIN, DOOR_WIDTH, DOOR_WORDS, doorSpan, lookOf, makeWallCommand, spineOf, wallGeometry, wallMetaOf, type DoorKind, type WallDoor, type WallGeom, type WallMeta, type WallSeg } from "./wall";

const mmText = (v: number) => `${Math.round(v)} mm`;

/** The segment of wall `id` nearest to `p`: where along it, which side of the spine, and how far from the spine. */
export function wallSegmentAt(doc: Doc, id: string, p: Vec): { seg: WallSeg; u: number; side: 1 | -1; dist: number } | null {
  const w = wallMetaOf(doc.entities[id]);
  const spine = spineOf(doc, id);
  const geo = w && spine ? wallGeometry(spine.points, spine.closed, w) : null;
  if (!geo) return null;
  let best: { seg: WallSeg; u: number; side: 1 | -1; dist: number } | null = null;
  const mid = (geo.offL + geo.offR) / 2;
  for (const s of geo.segs) {
    const raw = (p.x - s.a.x) * s.d.x + (p.y - s.a.y) * s.d.y;
    const u = Math.min(s.len, Math.max(0, raw));
    const across = (p.x - s.a.x) * s.n.x + (p.y - s.a.y) * s.n.y;
    // how far the point is outside the body of this wall (0 inside it): along the wall past its ends, and across it
    const along = raw < 0 ? -raw : raw > s.len ? raw - s.len : 0;
    const out = Math.max(0, geo.offR - across, across - geo.offL);
    const dist = Math.hypot(along, out) + Math.abs(across - mid) * 1e-6; // inside two walls (a corner): the nearer middle
    if (!best || dist < best.dist) best = { seg: s, u, side: across - mid >= 0 ? 1 : -1, dist };
  }
  return best;
}

/** The area of door `door` across its wall: from the far face to the end of its swing (or drum), in segment terms. */
function doorReach(geo: WallGeom, door: WallDoor): { lo: number; hi: number } {
  const face = door.side > 0 ? geo.offL : geo.offR;
  const other = door.side > 0 ? geo.offR : geo.offL;
  const reach = door.kind === "roller" ? 500 : door.kind === "double" ? door.width / 2 : door.width;
  return { lo: Math.min(other, face + door.side * reach), hi: Math.max(other, face + door.side * reach) };
}

/** The door of wall `id` at point `p` (in its opening, or in the sweep of its door), or null. */
export function doorAt(doc: Doc, id: string, p: Vec): WallDoor | null {
  const w = wallMetaOf(doc.entities[id]);
  const spine = spineOf(doc, id);
  const geo = w && spine ? wallGeometry(spine.points, spine.closed, w) : null;
  if (!w || !geo) return null;
  for (const door of w.doors) {
    const span = doorSpan(geo, door);
    if (!span) continue;
    const s = span.seg;
    const u = (p.x - s.a.x) * s.d.x + (p.y - s.a.y) * s.d.y;
    const across = (p.x - s.a.x) * s.n.x + (p.y - s.a.y) * s.n.y;
    const { lo, hi } = doorReach(geo, door);
    if (u >= span.ua - 1e-6 && u <= span.ub + 1e-6 && across >= lo - 1e-6 && across <= hi + 1e-6) return door;
  }
  return null;
}

/**
 * The outline of door `doorId` of wall `id` (its opening and its swing), as four world points, for the selection
 * mark. With `at`, the outline of the door with its centre there (the door while it is dragged). Null when no door.
 */
export function doorOutline(doc: Doc, id: string, doorId: string, at?: number): Vec[] | null {
  const w = wallMetaOf(doc.entities[id]);
  const spine = spineOf(doc, id);
  const geo = w && spine ? wallGeometry(spine.points, spine.closed, w) : null;
  const door = w?.doors.find((d) => d.id === doorId);
  const span = geo && door ? doorSpan(geo, at === undefined ? door : { ...door, at }) : null;
  if (!geo || !door || !span) return null;
  const s = span.seg;
  const { lo, hi } = doorReach(geo, door);
  const pt = (u: number, v: number): Vec => ({ x: s.a.x + s.d.x * u + s.n.x * v, y: s.a.y + s.d.y * u + s.n.y * v });
  return [pt(span.ua, lo), pt(span.ub, lo), pt(span.ub, hi), pt(span.ua, hi)];
}

/**
 * The centre nearest to `want` where a door `width` wide fits in wall `seg`: between the corners, and clear of the
 * other doors of that wall (except `exceptId`). Null when the door fits nowhere in that wall.
 *
 * 3.21.0. Owner, 2026-10-21: "I tried to add a door in at the end of a wall and it didn't appear". A door near a
 * corner that another door already used was refused, and the reason showed only in the status line. Now the door goes
 * to the nearest place where it fits.
 */
export function nearestDoorPlace(geo: WallGeom, w: WallMeta, seg: number, width: number, want: number, exceptId?: string): number | null {
  const s = geo.segs[seg];
  if (!s) return null;
  const lo = s.uMin + DOOR_MARGIN + width / 2;
  const hi = s.uMax - DOOR_MARGIN - width / 2;
  if (hi < lo - 1e-9) return null;
  // a door centre may not come nearer to another opening than half its own width
  const blocked = w.doors
    .filter((o) => o.id !== exceptId && o.seg === seg)
    .map((o) => doorSpan(geo, o))
    .filter((sp): sp is NonNullable<typeof sp> => !!sp)
    .map((sp) => [sp.ua - width / 2, sp.ub + width / 2] as const)
    .sort((a, b) => a[0] - b[0]);
  const free: Array<[number, number]> = [];
  let cur = lo;
  for (const [a, b] of blocked) {
    if (a > cur) free.push([cur, Math.min(a, hi)]);
    cur = Math.max(cur, b);
  }
  if (cur <= hi + 1e-9) free.push([cur, hi]);
  let best: number | null = null;
  for (const [a, b] of free) {
    if (b < a - 1e-9) continue;
    const u = Math.min(b, Math.max(a, want));
    if (best === null || Math.abs(u - want) < Math.abs(best - want)) best = u;
  }
  return best;
}

/**
 * 3.21.0: where door `doorId` goes when it is dragged so that the point grabbed is at `p`. `grab` is how far along the
 * wall the grab point was from the centre of the door. The door stays in its own wall; its near side goes in steps of
 * `step` mm from the corner; it stops at the corners and jumps past another door to the nearer free place.
 */
export function slideDoorAt(doc: Doc, id: string, doorId: string, p: Vec, grab: number, step = 10): number | null {
  const w = wallMetaOf(doc.entities[id]);
  const spine = spineOf(doc, id);
  const geo = w && spine ? wallGeometry(spine.points, spine.closed, w) : null;
  const door = w?.doors.find((d) => d.id === doorId);
  const s = door ? geo?.segs[door.seg] : undefined;
  if (!w || !geo || !door || !s) return null;
  const u = (p.x - s.a.x) * s.d.x + (p.y - s.a.y) * s.d.y - grab;
  const near = step > 0 ? Math.round((u - door.width / 2) / step) * step : u - door.width / 2;
  return nearestDoorPlace(geo, w, door.seg, door.width, near + door.width / 2, door.id);
}

function metaPatch(doc: Doc, id: string, next: WallMeta, label: string): Command {
  const g = doc.entities[id] as GroupEntity;
  return { kind: "batch", label, commands: [{ kind: "updateEntities", patches: [{ id, before: { meta: structuredClone(g.meta) }, after: { meta: { ...structuredClone(g.meta ?? {}), wall: next } } }] }] };
}

/** The reason door `door` cannot be in wall `w` (it does not fit, or it overlaps another door), or null. */
function doorProblem(doc: Doc, id: string, w: WallMeta, door: WallDoor): string | null {
  const spine = spineOf(doc, id)!;
  const geo = wallGeometry(spine.points, spine.closed, w);
  const seg = geo?.segs[door.seg];
  if (!geo || !seg) return "That wall has no such part.";
  if (!(door.width >= 100)) return "A door opening is 100 mm or more.";
  const room = seg.uMax - seg.uMin;
  if (room < door.width - 1e-9) return `This wall is ${mmText(room)} between its corners. A ${mmText(door.width)} door does not fit. Use a narrower door or a longer wall.`;
  const span = doorSpan(geo, door)!;
  for (const o of w.doors) {
    if (o.id === door.id || o.seg !== door.seg) continue;
    const os = doorSpan(geo, o);
    if (os && span.ua < os.ub - 1e-6 && span.ub > os.ua + 1e-6) return `This door overlaps the ${DOOR_WORDS[o.kind].toLowerCase()} on that wall. Move it, or take the other door away first.`;
  }
  return null;
}

/**
 * ONE command that adds a door of `kind` to wall `id`, centred where `p` is along the wall, opening to the side of the
 * wall that `p` is on. Near a corner the door stops at the corner. On another door it goes to the nearest place where
 * it fits (`moved` is then true: another door moved it). A sentence instead when it fits nowhere in that wall.
 */
export function addDoorCommand(doc: Doc, id: string, p: Vec, kind: DoorKind, width = DOOR_WIDTH[kind]): { cmd: Command; door: WallDoor; moved: boolean } | string {
  const w = wallMetaOf(doc.entities[id]);
  const hit = wallSegmentAt(doc, id, p);
  const spine = spineOf(doc, id);
  const geo = w && spine ? wallGeometry(spine.points, spine.closed, w) : null;
  if (!w || !hit || !geo) return "Select a wall.";
  const door: WallDoor = { id: newId(), kind, seg: hit.seg.index, at: hit.u, width, side: hit.side, hinge: "start" };
  const room = hit.seg.uMax - hit.seg.uMin;
  if (room < width - 1e-9) return `Wall ${hit.seg.index + 1} is ${mmText(room)} between its corners. A ${mmText(width)} door does not fit. Use a narrower door or a longer wall.`;
  const at = nearestDoorPlace(geo, w, hit.seg.index, width, hit.u);
  if (at === null) return `Wall ${hit.seg.index + 1} has no free ${mmText(width)} between its corners and its other doors. Move or take away a door first.`;
  door.at = at;
  const why = doorProblem(doc, id, { ...w, doors: [...w.doors, door] }, door);
  if (why) return why;
  // where it would be with no other door in the wall: the click, stopped at the corners
  const alone = Math.min(hit.seg.uMax - DOOR_MARGIN - width / 2, Math.max(hit.seg.uMin + DOOR_MARGIN + width / 2, hit.u));
  return { cmd: metaPatch(doc, id, { ...structuredClone(w), doors: [...structuredClone(w.doors), door] }, "add door"), door, moved: Math.abs(at - alone) > 1 };
}

/** ONE command that changes door `doorId` (kind, width, place, side, hinge). A sentence when it cannot. */
export function updateDoorCommand(doc: Doc, id: string, doorId: string, change: Partial<Omit<WallDoor, "id" | "seg">>): Command | string {
  const w = wallMetaOf(doc.entities[id]);
  const old = w?.doors.find((d) => d.id === doorId);
  if (!w || !old) return "That door is not in the wall.";
  const door: WallDoor = { ...old, ...change };
  if (change.kind && change.width === undefined && old.width === DOOR_WIDTH[old.kind]) door.width = DOOR_WIDTH[change.kind];
  if (JSON.stringify(door) === JSON.stringify(old)) return "The door is like that already.";
  const doors = w.doors.map((d) => (d.id === doorId ? door : structuredClone(d)));
  const why = doorProblem(doc, id, { ...w, doors }, door);
  if (why) return why;
  return metaPatch(doc, id, { ...structuredClone(w), doors }, "change door");
}

/** ONE command that takes door `doorId` out: the wall is whole there again. */
export function removeDoorCommand(doc: Doc, id: string, doorId: string): Command | string {
  const w = wallMetaOf(doc.entities[id]);
  if (!w || !w.doors.some((d) => d.id === doorId)) return "That door is not in the wall.";
  return metaPatch(doc, id, { ...structuredClone(w), doors: w.doors.filter((d) => d.id !== doorId).map((d) => structuredClone(d)) }, "remove door");
}

/** ONE command that changes the thickness, the fill or the line the wall is drawn by. A sentence when it cannot. */
export function setWallCommand(doc: Doc, id: string, change: Partial<Pick<WallMeta, "thickness" | "fill" | "justify">>): Command | string {
  const w = wallMetaOf(doc.entities[id]);
  if (!w) return "Select a wall.";
  if (change.thickness !== undefined && !(change.thickness >= 10 && change.thickness <= 2000)) return "A wall is 10 mm to 2000 mm thick.";
  const next: WallMeta = { ...structuredClone(w), ...structuredClone(change) };
  if (JSON.stringify(next) === JSON.stringify(w)) return "The wall is like that already.";
  // a thicker wall makes the corners longer: a door that no longer fits says so
  for (const d of next.doors) {
    const why = doorProblem(doc, id, next, d);
    if (why) return `At this thickness a door does not fit: ${why}`;
  }
  return metaPatch(doc, id, next, "wall");
}

/**
 * ONE command that makes wall `seg` of run `id` `length` mm long (along the spine). In an open run the walls after it
 * move along. In a room the walls after it move along up to the first wall that runs back, which gets longer or shorter
 * by the same amount: a rectangular room stays rectangular. A sentence when it cannot.
 */
export function setSegmentLengthCommand(doc: Doc, id: string, seg: number, length: number): Command | string {
  const g = doc.entities[id];
  const w = wallMetaOf(g);
  const spine = spineOf(doc, id);
  if (!w || !spine || g?.type !== "group") return "Select a wall.";
  if (!(length >= 10)) return "A wall is 10 mm or longer.";
  const geo = wallGeometry(spine.points, spine.closed, w);
  const s = geo?.segs[seg];
  if (!geo || !s) return "That wall has no such part.";
  const delta = length - s.len;
  if (Math.abs(delta) < 1e-9) return "The wall is that long already.";
  const n = spine.points.length;
  const shift = { x: s.d.x * delta, y: s.d.y * delta };
  // which points move: in an open run, every point after the wall. In a room, the corners after the wall up to the first
  // wall that runs back the other way: a rectangle stays a rectangle (the far wall moves, the walls across it lengthen)
  const moving = new Set<number>();
  if (!spine.closed) for (let k = seg + 1; k < n; k++) moving.add(k);
  else {
    for (let k = (seg + 1) % n; k !== seg; k = (k + 1) % n) {
      moving.add(k);
      const next = geo.segs[k]!;
      if (next.d.x * s.d.x + next.d.y * s.d.y < -1e-6) break;
    }
  }
  const points = spine.points.map((p, k) => (moving.has(k) ? { x: p.x + shift.x, y: p.y + shift.y } : { ...p }));
  const spineId = (g as GroupEntity).children[0]!;
  const next = { ...w, doors: w.doors.map((d) => structuredClone(d)) };
  const geo2 = wallGeometry(points, spine.closed, next);
  if (!geo2) return "That length leaves a wall with no length.";
  for (const d of next.doors) {
    const sp = doorSpan(geo2, d);
    if (!sp) return `At that length a ${DOOR_WORDS[d.kind].toLowerCase()} does not fit. Take it away or make it narrower first.`;
  }
  return { kind: "batch", label: "wall length", commands: [{ kind: "updateEntities", patches: [{ id: spineId, before: { points: structuredClone(spine.points) }, after: { points } }] }] };
}

// ---------------------------------------------------------------- join, split, ends and corners (3.23.0) --
// Owner, 2026-10-21: "update the wall tool so walls can attach to other walls and the intersection needs to look neat,
// also allow intersections to be detached so a corner can be broken in two separate walls, and intersecting walls can
// be separated at their intersecting lines, allow walls to be lengthened or shortened and rooms to be adjusted via
// their corners". The joins themselves are found from the drawing (wall.ts wallNetwork): two ends at one point meet
// in a mitre; an end on another wall is cut at its face (a T); walls that cross show one outline.

const sub2 = (a: Vec, b: Vec): Vec => ({ x: a.x - b.x, y: a.y - b.y });
const len2 = (v: Vec) => Math.hypot(v.x, v.y);
/** The shortest wall that a split, an end or a corner may leave, mm. */
export const MIN_WALL = 10;

/**
 * Where a point near walls attaches (the Wall tool, and an end or a corner that is dragged): the free end of another
 * run (the two then meet in a mitre), or the centre line of another wall (a T). `tol`: how near, mm. Walls in `skip`
 * are not attached to. Null: nothing near.
 */
export function wallAttachPoint(doc: Doc, p: Vec, tol: number, skip: ReadonlySet<string> = new Set()): { p: Vec; kind: "end" | "wall"; wallId: string; along?: Vec } | null {
  let end: { p: Vec; d: number; wallId: string } | null = null;
  let body: { p: Vec; d: number; wallId: string; along: Vec } | null = null;
  for (const e of Object.values(doc.entities)) {
    if (e.type !== "group" || e.hidden || skip.has(e.id)) continue;
    const w = wallMetaOf(e);
    const spine = spineOf(doc, e.id);
    const geo = w && spine ? wallGeometry(spine.points, spine.closed, w) : null;
    if (!w || !spine || !geo) continue;
    if (!geo.closed) {
      for (const q of [spine.points[0]!, spine.points[spine.points.length - 1]!]) {
        const d = len2(sub2(q, p));
        if (d <= tol && (!end || d < end.d)) end = { p: { ...q }, d, wallId: e.id };
      }
    }
    const c = (geo.offL + geo.offR) / 2;
    for (const s of geo.segs) {
      const u = (p.x - s.a.x) * s.d.x + (p.y - s.a.y) * s.d.y;
      if (u < 0 || u > s.len) continue;
      const v = (p.x - s.a.x) * s.n.x + (p.y - s.a.y) * s.n.y - c;
      const d = Math.abs(v);
      if (d > w.thickness / 2 + tol) continue;
      if (!body || d < body.d) body = { p: { x: s.a.x + s.d.x * u + s.n.x * c, y: s.a.y + s.d.y * u + s.n.y * c }, d, wallId: e.id, along: { ...s.d } };
    }
  }
  if (end) return { p: end.p, kind: "end", wallId: end.wallId };
  if (body) return { p: body.p, kind: "wall", wallId: body.wallId, along: body.along };
  return null;
}

/** The spine distance of each door's centre and its span, on geometry `geo`. */
function doorPlaces(geo: WallGeom, doors: WallDoor[]): Array<{ door: WallDoor; g: number; ga: number; gb: number }> {
  return doors.map((d) => {
    const sp = doorSpan(geo, d);
    const s = geo.segs[d.seg];
    const uc = sp ? sp.uc : d.at;
    const g = (s?.start ?? 0) + uc;
    return { door: d, g, ga: g - d.width / 2, gb: g + d.width / 2 };
  });
}

/** Door `d` put back on a run of points, at spine distance g: its segment and its place along it. */
function doorOn(points: Vec[], closed: boolean, w: WallMeta, d: WallDoor, g: number): WallDoor {
  const geo = wallGeometry(points, closed, w)!;
  let seg = geo.segs[geo.segs.length - 1]!;
  for (const s of geo.segs) {
    if (g <= s.start + s.len + 1e-9) {
      seg = s;
      break;
    }
  }
  return { ...structuredClone(d), seg: seg.index, at: g - seg.start };
}

/**
 * ONE command that splits wall run `id` at spine distance `gs` (from the start of the run): an open run becomes two
 * runs, a room becomes one open run that starts and ends there. The first part keeps the wall's id. Where the split is
 * at a corner, the two new ends meet in a mitre, so the corner looks the same until one wall is moved. A sentence
 * instead when it cannot be split there (an end, a door in the way, a part shorter than 10 mm).
 */
export function splitWallCommand(doc: Doc, id: string, gs: number, label = "split wall"): { cmd: Command; ids: string[] } | string {
  const g = doc.entities[id];
  const w = wallMetaOf(g);
  const spine = spineOf(doc, id);
  const geo = w && spine ? wallGeometry(spine.points, spine.closed, w) : null;
  if (!w || !spine || !geo || g?.type !== "group") return "Select a wall.";
  const total = geo.total;
  const P = geo.segs.map((s) => s.a);
  if (!geo.closed) P.push(geo.segs[geo.segs.length - 1]!.b);
  const endWords = "That is at the end of the wall. Split a wall between its ends, or at a corner.";
  if (!geo.closed && (gs < MIN_WALL - 1e-6 || gs > total - MIN_WALL + 1e-6)) return endWords;
  const places = doorPlaces(geo, w.doors);
  const inDoor = places.find((x) =>
    geo.closed ? [gs, gs + total, gs - total].some((t) => t > x.ga + 1e-6 && t < x.gb - 1e-6) : gs > x.ga + 1e-6 && gs < x.gb - 1e-6
  );
  if (inDoor) return `A ${DOOR_WORDS[inDoor.door.kind].toLowerCase()} is at that place. Move the door first.`;
  // the split point, and whether it is a corner
  let seg = geo.segs[geo.segs.length - 1]!;
  for (const s of geo.segs) {
    if (gs <= s.start + s.len + 1e-9) {
      seg = s;
      break;
    }
  }
  const u = gs - seg.start;
  const S = { x: seg.a.x + seg.d.x * u, y: seg.a.y + seg.d.y * u };
  const k0 = seg.index; // the corner at the start of the segment
  const atCornerStart = u <= 1e-6;
  const atCornerEnd = u >= seg.len - 1e-6;
  const n = P.length;
  const spineId = (g as GroupEntity).children[0]!;
  if (geo.closed) {
    // one open run from S round to S
    const kS = atCornerStart ? k0 : atCornerEnd ? (k0 + 1) % n : -1;
    const ring: Vec[] = [];
    if (kS >= 0) for (let i = 0; i <= n; i++) ring.push({ ...P[(kS + i) % n]! });
    else {
      ring.push({ ...S });
      for (let i = 1; i <= n; i++) ring.push({ ...P[(k0 + i) % n]! });
      ring.push({ ...S });
    }
    const doors = places.map((x) => doorOn(ring, false, w, x.door, (((x.g - gs) % total) + total) % total));
    const meta: WallMeta = { ...structuredClone(w), closed: false, doors };
    return {
      cmd: {
        kind: "batch",
        label,
        commands: [
          { kind: "updateEntities", patches: [{ id: spineId, before: { points: structuredClone(spine.points), closed: spine.closed }, after: { points: ring, closed: false } }] },
          { kind: "updateEntities", patches: [{ id, before: { meta: structuredClone(g.meta) }, after: { meta: { ...structuredClone(g.meta ?? {}), wall: meta } } }] }
        ]
      },
      ids: [id]
    };
  }
  // an open run: two runs
  const k = atCornerStart ? k0 : atCornerEnd ? k0 + 1 : -1;
  const A: Vec[] = k >= 0 ? P.slice(0, k + 1).map((q) => ({ ...q })) : [...P.slice(0, k0 + 1).map((q) => ({ ...q })), S];
  const B: Vec[] = k >= 0 ? P.slice(k).map((q) => ({ ...q })) : [{ ...S }, ...P.slice(k0 + 1).map((q) => ({ ...q }))];
  if (A.length < 2 || B.length < 2) return endWords;
  const doorsA = places.filter((x) => x.g < gs).map((x) => doorOn(A, false, w, x.door, x.g));
  const doorsB = places.filter((x) => x.g >= gs).map((x) => doorOn(B, false, w, x.door, x.g - gs));
  const layerId = doc.entities[spineId]?.layerId ?? g.layerId;
  const second = makeWallCommand(doc, layerId, B, false, { thickness: w.thickness, justify: w.justify, fill: structuredClone(w.fill), doors: doorsB }, lookOf(doc, g));
  if (!second) return "That leaves a wall with no length.";
  const metaA: WallMeta = { ...structuredClone(w), closed: false, doors: doorsA };
  return {
    cmd: {
      kind: "batch",
      label,
      commands: [
        { kind: "updateEntities", patches: [{ id: spineId, before: { points: structuredClone(spine.points), closed: spine.closed }, after: { points: A, closed: false } }] },
        { kind: "updateEntities", patches: [{ id, before: { meta: structuredClone(g.meta) }, after: { meta: { ...structuredClone(g.meta ?? {}), wall: metaA } } }] },
        second.cmd
      ]
    },
    ids: [id, second.groupId]
  };
}

/** The spine corner of wall `id` near `p` (within `tol` mm): its index and its spine distance; null when none. */
export function wallCornerAt(doc: Doc, id: string, p: Vec, tol: number): { k: number; g: number } | null {
  const w = wallMetaOf(doc.entities[id]);
  const spine = spineOf(doc, id);
  const geo = w && spine ? wallGeometry(spine.points, spine.closed, w) : null;
  if (!geo) return null;
  let best: { k: number; g: number; d: number } | null = null;
  for (const s of geo.segs) {
    if (!geo.closed && s.index === 0) continue; // the start of an open run is an end, not a corner
    const d = len2(sub2(s.a, p));
    if (d <= tol && (!best || d < best.d)) best = { k: s.index, g: s.start, d };
  }
  return best ? { k: best.k, g: best.g } : null;
}

/**
 * Where the spine of wall `id` meets the centre line of another wall: each crossing or touching point, with its spine
 * distance and the other wall. Not near the ends of wall `id` (an end on another wall is a join already).
 */
export function wallMeetings(doc: Doc, id: string): Array<{ p: Vec; g: number; withId: string }> {
  const w = wallMetaOf(doc.entities[id]);
  const spine = spineOf(doc, id);
  const geo = w && spine ? wallGeometry(spine.points, spine.closed, w) : null;
  if (!geo) return [];
  const out: Array<{ p: Vec; g: number; withId: string }> = [];
  for (const e of Object.values(doc.entities)) {
    if (e.type !== "group" || e.id === id || e.hidden) continue;
    const ow = wallMetaOf(e);
    const os = spineOf(doc, e.id);
    const og = ow && os ? wallGeometry(os.points, os.closed, ow) : null;
    if (!og) continue;
    const c = (og.offL + og.offR) / 2;
    for (const s of geo.segs) {
      for (const t of og.segs) {
        const ta = { x: t.a.x + t.n.x * c, y: t.a.y + t.n.y * c };
        const den = s.d.x * t.d.y - s.d.y * t.d.x;
        if (Math.abs(den) < 1e-9) continue;
        const q = sub2(ta, s.a);
        const u = (q.x * t.d.y - q.y * t.d.x) / den; // along s
        const v = (q.x * s.d.y - q.y * s.d.x) / den; // along t
        if (u < -1e-6 || u > s.len + 1e-6 || v < -1e-6 || v > t.len + 1e-6) continue;
        const gpos = s.start + u;
        if (!geo.closed && (gpos < MIN_WALL || gpos > geo.total - MIN_WALL)) continue;
        out.push({ p: { x: s.a.x + s.d.x * u, y: s.a.y + s.d.y * u }, g: gpos, withId: e.id });
      }
    }
  }
  return out;
}

/** True when a closed run of 4 points has 4 right angles (a rectangular room). */
export function isRectangle(points: Vec[], closed: boolean): boolean {
  if (!closed || points.length !== 4) return false;
  for (let k = 0; k < 4; k++) {
    const a = sub2(points[(k + 1) % 4]!, points[k]!);
    const b = sub2(points[(k + 2) % 4]!, points[(k + 1) % 4]!);
    const la = len2(a), lb = len2(b);
    if (la < 1e-9 || lb < 1e-9 || Math.abs((a.x * b.x + a.y * b.y) / (la * lb)) > 1e-6) return false;
  }
  return true;
}

/**
 * The spine points of a wall when its point k is dragged to `to`:
 *   - an END of an open run slides along its wall (lengthen or shorten), unless `free`;
 *   - a CORNER of a rectangular room keeps the room rectangular: the opposite corner stays and the two corners next
 *     to it move with it, unless `free`;
 *   - any other corner goes to `to`.
 * Null when a wall would be shorter than 10 mm.
 */
export function movedWallPoints(points: Vec[], closed: boolean, k: number, to: Vec, free = false): Vec[] | null {
  const n = points.length;
  const P = points.map((q) => ({ ...q }));
  const isEnd = !closed && (k === 0 || k === n - 1);
  if (isEnd && !free) {
    const adj = P[k === 0 ? 1 : n - 2]!;
    const d0 = sub2(P[k]!, adj);
    const L = len2(d0);
    if (L < 1e-9) return null;
    const d = { x: d0.x / L, y: d0.y / L };
    const along = (to.x - adj.x) * d.x + (to.y - adj.y) * d.y;
    P[k] = { x: adj.x + d.x * along, y: adj.y + d.y * along };
  } else if (!free && isRectangle(points, closed)) {
    const A = P[(k + 2) % 4]!; // the opposite corner stays
    const ua = sub2(P[(k + 1) % 4]!, A);
    const va = sub2(P[(k + 3) % 4]!, A);
    const lu = len2(ua), lv = len2(va);
    const u = { x: ua.x / lu, y: ua.y / lu };
    const v = { x: va.x / lv, y: va.y / lv };
    const q = sub2(to, A);
    const cu = q.x * u.x + q.y * u.y;
    const cv = q.x * v.x + q.y * v.y;
    P[(k + 1) % 4] = { x: A.x + u.x * cu, y: A.y + u.y * cu };
    P[(k + 3) % 4] = { x: A.x + v.x * cv, y: A.y + v.y * cv };
    P[k] = { x: A.x + u.x * cu + v.x * cv, y: A.y + u.y * cu + v.y * cv };
  } else P[k] = { ...to };
  const count = closed ? n : n - 1;
  for (let i = 0; i < count; i++) if (len2(sub2(P[(i + 1) % n]!, P[i]!)) < MIN_WALL) return null;
  return P;
}

/** ONE command that gives wall `id` these spine points. A sentence when a door would no longer fit. */
export function setWallPointsCommand(doc: Doc, id: string, points: Vec[], label = "wall"): Command | string {
  const g = doc.entities[id];
  const w = wallMetaOf(g);
  const spine = spineOf(doc, id);
  if (!w || !spine || g?.type !== "group") return "Select a wall.";
  const geo = wallGeometry(points, spine.closed, w);
  if (!geo) return "That leaves a wall with no length.";
  for (const d of w.doors) {
    if (!doorSpan(geo, d)) return `At that size the ${DOOR_WORDS[d.kind].toLowerCase()} in wall ${d.seg + 1} does not fit. Make the wall longer, or take the door away first.`;
  }
  const spineId = (g as GroupEntity).children[0]!;
  return { kind: "batch", label, commands: [{ kind: "updateEntities", patches: [{ id: spineId, before: { points: structuredClone(spine.points) }, after: { points: points.map((q) => ({ ...q })) } }] }] };
}
